import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import videoCreatorApi from '../../utils/videoCreatorApi';
import HelpVideoPlayerDialog from '../help/HelpVideoPlayerDialog';

const TAB_COLORS = {
  0: { bg: '#1565c0', soft: '#e3f2fd' },
  1: { bg: '#2e7d32', soft: '#e8f5e9' },
  2: { bg: '#6a1b9a', soft: '#f3e5f5' },
};

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function statusChip(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'succeeded') return <Chip size="small" color="success" label="completed" />;
  if (s === 'running' || s === 'queued' || s === 'in_progress')
    return <Chip size="small" color="info" label={s === 'queued' ? 'queued' : 'in progress'} />;
  if (s === 'errored' || s === 'failed') return <Chip size="small" color="error" label="errored" />;
  return <Chip size="small" label={s || '—'} />;
}

export default function AdminHelpSetup() {
  const [tab, setTab] = useState(0);
  const [modules, setModules] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [voices, setVoices] = useState([]);
  const [mode, setMode] = useState('one'); // one | multi
  const [selectedOne, setSelectedOne] = useState('contacts');
  const [selectedMulti, setSelectedMulti] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [overrideDlg, setOverrideDlg] = useState(null);
  const [play, setPlay] = useState(null);
  const [moduleVersions, setModuleVersions] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, j, v] = await Promise.all([
        videoCreatorApi.get('/v1/modules'),
        videoCreatorApi.get('/v1/jobs?limit=100'),
        videoCreatorApi.get('/v1/voices'),
      ]);
      setModules(m.data?.data || []);
      setJobs(j.data?.data || []);
      setVoices(v.data?.data || []);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load Help Setup');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      videoCreatorApi
        .get('/v1/jobs?limit=100')
        .then((r) => setJobs(r.data?.data || []))
        .catch(() => null);
      videoCreatorApi
        .get('/v1/modules')
        .then((r) => setModules(r.data?.data || []))
        .catch(() => null);
    }, 5000);
    return () => clearInterval(t);
  }, [load]);

  const selectedIds = useMemo(() => {
    if (mode === 'one') return selectedOne ? [selectedOne] : [];
    return selectedMulti.slice(0, 3);
  }, [mode, selectedOne, selectedMulti]);

  const selectedCooldownBlocks = useMemo(() => {
    return selectedIds
      .map((id) => modules.find((m) => m.id === id))
      .filter((m) => m && m.cooldown && m.cooldown.allowed === false)
      .map((m) => ({
        module_id: m.id,
        title: m.title,
        message: m.cooldown.message,
        cooldown: m.cooldown,
      }));
  }, [selectedIds, modules]);

  const toggleMulti = (id) => {
    setSelectedMulti((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const doGenerate = async ({ override = false } = {}) => {
    if (!override && selectedCooldownBlocks.length) {
      setOverrideDlg({ blocked: selectedCooldownBlocks, notes });
      return;
    }
    setGenerating(true);
    setErr('');
    setMsg('');
    try {
      const body = { modules: selectedIds, notes: notes || undefined, override: Boolean(override) };
      const r = await videoCreatorApi.post('/v1/videos/generate-batch', body);
      const data = r.data?.data;
      const n = data?.jobs?.length || 0;
      const blocked = data?.blocked || [];
      setMsg(
        `Queued ${n} job(s)${blocked.length ? `; ${blocked.length} still blocked by cooldown` : ''}. Versions use V0001, V0002, …`
      );
      if (blocked.length) {
        setOverrideDlg({ blocked, notes });
      }
      await load();
    } catch (e) {
      const payload = e.response?.data;
      if (e.response?.status === 409 || payload?.code === 'COOLDOWN') {
        setOverrideDlg({
          blocked: payload?.blocked || [
            {
              module_id: selectedIds[0],
              message: payload?.message,
              cooldown: payload?.cooldown,
            },
          ],
          notes,
        });
      } else {
        setErr(payload?.message || e.message || 'Generate failed');
      }
    } finally {
      setGenerating(false);
    }
  };

  const confirmOverride = async () => {
    setOverrideDlg(null);
    await doGenerate({ override: true });
  };

  const loadModuleVersions = async (moduleId) => {
    if (!moduleId) {
      setModuleVersions([]);
      return;
    }
    try {
      const r = await videoCreatorApi.get(`/v1/modules/${moduleId}/videos`);
      setModuleVersions((r.data?.data?.generated?.versions || r.data?.data?.videos || []).filter((v) => v.version));
    } catch {
      setModuleVersions([]);
    }
  };

  useEffect(() => {
    if (tab === 1 && mode === 'one' && selectedOne) loadModuleVersions(selectedOne);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedOne, mode, modules]);

  const deleteVersion = async (moduleId, version) => {
    if (!window.confirm(`Delete ${moduleId} ${version}? This cannot be undone. Cooldown stays in effect.`)) return;
    setDeleting(`${moduleId}:${version}`);
    try {
      await videoCreatorApi.delete(`/v1/modules/${moduleId}/versions/${version}`);
      setMsg(`Deleted ${moduleId} ${version}`);
      await load();
      await loadModuleVersions(moduleId);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const latestByModule = useMemo(() => {
    const map = {};
    for (const m of modules) {
      map[m.id] = m;
    }
    return map;
  }, [modules]);

  return (
    <Box sx={{ p: 2, width: '100%', maxWidth: '100%' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Help Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Administration control for ERP4C Product Help videos (erp4c-video-creator — no LLM tokens). Max 3 modules at
        once. Regenerating the same module within 90 days requires an explicit Override.
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': { fontWeight: 700, color: 'text.secondary', flex: 1 },
            '& .Mui-selected': { color: '#fff !important' },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab
            label="Generate Video"
            sx={{ bgcolor: tab === 0 ? TAB_COLORS[0].bg : TAB_COLORS[0].soft }}
          />
          <Tab
            label="Generation History"
            sx={{ bgcolor: tab === 1 ? TAB_COLORS[1].bg : TAB_COLORS[1].soft }}
          />
          <Tab
            label="Voices & Policy"
            sx={{ bgcolor: tab === 2 ? TAB_COLORS[2].bg : TAB_COLORS[2].soft }}
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {loading && <CircularProgress size={28} />}
          {err && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
              {err}
            </Alert>
          )}
          {msg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
              {msg}
            </Alert>
          )}

          <TabPanel value={tab} index={0}>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selection mode
              </Typography>
              <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
                <FormControlLabel value="one" control={<Radio />} label="One module" />
                <FormControlLabel value="multi" control={<Radio />} label="Multiple modules (max 3)" />
              </RadioGroup>
            </FormControl>

            {mode === 'one' ? (
              <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 420 }}>
                <InputLabel>Module / entity</InputLabel>
                <Select
                  label="Module / entity"
                  value={selectedOne}
                  onChange={(e) => setSelectedOne(e.target.value)}
                >
                  {modules.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.title}
                      {!m.can_auto_generate ? ' (recorder soon)' : ''}
                      {m.latest_version ? ` · ${m.latest_version}` : ''}
                      {m.cooldown && m.cooldown.allowed === false ? ' · cooldown' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select up to 3 modules ({selectedMulti.length}/3)
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {modules.map((m) => (
                    <FormControlLabel
                      key={m.id}
                      control={
                        <Checkbox
                          checked={selectedMulti.includes(m.id)}
                          onChange={() => toggleMulti(m.id)}
                          disabled={!selectedMulti.includes(m.id) && selectedMulti.length >= 3}
                        />
                      }
                      label={`${m.title}${m.can_auto_generate ? '' : ' *'}${
                        m.cooldown && m.cooldown.allowed === false ? ' ⏳' : ''
                      }`}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  * Recorder not implemented yet — generate will be rejected for those modules.
                </Typography>
              </Box>
            )}

            {selectedCooldownBlocks.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>90-day cooldown active</strong> for:{' '}
                {selectedCooldownBlocks.map((b) => b.title || b.module_id).join(', ')}. Generate is blocked until you
                click <strong>Override &amp; regenerate</strong> (resets the clock) or wait{' '}
                {selectedCooldownBlocks[0]?.cooldown?.days_remaining ?? 90} day(s).
              </Alert>
            )}

            <TextField
              label="Notes (optional)"
              fullWidth
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 2, maxWidth: 520 }}
            />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                disabled={generating || !selectedIds.length || selectedCooldownBlocks.length > 0}
                onClick={() => doGenerate({ override: false })}
              >
                {generating ? 'Queuing…' : 'Generate'}
              </Button>
              {selectedCooldownBlocks.length > 0 && (
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={generating || !selectedIds.length}
                  onClick={() => setOverrideDlg({ blocked: selectedCooldownBlocks, notes })}
                >
                  Override &amp; regenerate…
                </Button>
              )}
              {generating && <CircularProgress size={22} />}
            </Stack>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Job status
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Module</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.slice(0, 25).map((j) => {
                  const title = latestByModule[j.module_id]?.title || j.module_id;
                  const result = j.result;
                  const ver = j.version || result?.version;
                  return (
                    <TableRow key={j.id} hover>
                      <TableCell>{title}</TableCell>
                      <TableCell>{ver || '—'}</TableCell>
                      <TableCell>{j.started_at ? new Date(j.started_at).toLocaleString() : '—'}</TableCell>
                      <TableCell>
                        {j.finished_at ? new Date(j.finished_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        {statusChip(j.status)}
                        {j.error ? (
                          <Typography variant="caption" color="error" display="block">
                            {String(j.error).slice(0, 120)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {result?.url ? (
                            <Button
                              size="small"
                              onClick={() =>
                                setPlay({
                                  video: result,
                                  moduleId: j.module_id,
                                  moduleTitle: title,
                                })
                              }
                            >
                              Play
                            </Button>
                          ) : null}
                          {ver && (j.status === 'completed' || j.status === 'succeeded') ? (
                            <Button
                              size="small"
                              color="error"
                              disabled={deleting === `${j.module_id}:${ver}`}
                              onClick={() => deleteVersion(j.module_id, ver)}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!jobs.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No generation jobs yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Module status and delete for stored versions. Cooldown is not cleared by delete.
            </Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Module</TableCell>
                  <TableCell>Latest version</TableCell>
                  <TableCell>Can generate</TableCell>
                  <TableCell>Cooldown</TableCell>
                  <TableCell align="right">Videos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((m) => {
                  const c = m.cooldown || {};
                  return (
                    <TableRow key={m.id}>
                      <TableCell>{m.title}</TableCell>
                      <TableCell>{m.latest_version || '—'}</TableCell>
                      <TableCell>{m.can_auto_generate ? 'Yes' : 'Soon'}</TableCell>
                      <TableCell>
                        {c.last_generated_at
                          ? c.allowed
                            ? `OK (${c.days_ago ?? 0}d ago)`
                            : `Blocked · ${c.days_remaining}d left`
                          : 'Never generated'}
                      </TableCell>
                      <TableCell align="right">{m.video_count}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Typography variant="subtitle2" gutterBottom>
              Delete versions for selected module (one-module mode)
            </Typography>
            <FormControl size="small" sx={{ mb: 1, minWidth: 280 }}>
              <InputLabel>Module</InputLabel>
              <Select
                label="Module"
                value={selectedOne}
                onChange={(e) => {
                  setSelectedOne(e.target.value);
                  loadModuleVersions(e.target.value);
                }}
              >
                {modules.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {moduleVersions.map((v) => (
                  <TableRow key={v.version}>
                    <TableCell>{v.version}</TableCell>
                    <TableCell>{v.created_at ? new Date(v.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{v.duration_label || '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        disabled={deleting === `${selectedOne}:${v.version}`}
                        onClick={() => deleteVersion(selectedOne, v.version)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!moduleVersions.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No versions for this module.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>90-day policy:</strong> After a successful generate, the same module cannot be regenerated for 90
              days unless an administrator chooses <strong>Override &amp; regenerate</strong> (resets the clock).
              Versions are labeled <strong>V0001</strong>, <strong>V0002</strong>, …
            </Alert>
            <Typography variant="subtitle2" gutterBottom>
              Playback voices (3)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              US, British, and Indian English use clear neural speech prepared on play (cached). Offline Piper voices are
              used by default so playback does not depend on Microsoft Edge TTS. The product video is not re-recorded.
            </Typography>
            <Stack component="ul" sx={{ pl: 2, m: 0 }}>
              {voices.map((v) => (
                <Typography component="li" key={v.id} variant="body2">
                  {v.label}
                  {v.is_default ? ' — default' : ''}
                  {v.mode ? ` (${v.mode})` : ''}
                </Typography>
              ))}
            </Stack>
          </TabPanel>
        </Box>
      </Paper>

      <Dialog open={Boolean(overrideDlg)} onClose={() => setOverrideDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Already generated — override?</DialogTitle>
        <DialogContent>
          {(overrideDlg?.blocked || []).map((b) => (
            <Alert key={b.module_id || b.message} severity="warning" sx={{ mb: 1 }}>
              {b.message ||
                `You already generated ${b.title || b.module_id} ${b.cooldown?.days_ago ?? 0} day(s) ago. To regenerate now, override resets the 90-day clock to today.`}
            </Alert>
          ))}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Click <strong>Override &amp; generate</strong> to reset cooldown and create the next version, or{' '}
            <strong>Cancel</strong> to leave the module unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmOverride}>
            Override &amp; generate
          </Button>
        </DialogActions>
      </Dialog>

      {play && (
        <HelpVideoPlayerDialog
          open
          video={play.video}
          moduleId={play.moduleId}
          moduleTitle={play.moduleTitle}
          onClose={() => setPlay(null)}
        />
      )}
    </Box>
  );
}
